import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createTask, updateTask, getTask, getUsers, deleteAttachment, aiSuggestPriority, getDepartmentCategories, getDepartmentConfig, getSubcategories, getProjects, presignAttachment, registerS3Attachment, uploadToS3, getEligibleApprovers } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format } from 'date-fns';
import { ArrowLeft, CalendarIcon, Upload, File, Paperclip, Trash2, Sparkles, AlertTriangle, Users, X, FolderKanban } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Checkbox } from '../components/ui/checkbox';

const BRANDS = ['United Group', 'Unitile', 'UniVicoustic', 'U-Flex', 'Forza', 'UniPro'];
import { useDepartments } from '../hooks/useDepartments';
const DD_DEPT = 'Design & Development';
const APPROVAL_DEPARTMENTS = ['Design & Development', 'Marketing', 'Sales Coordinators'];
const PROJECT_DEPARTMENTS = ['Design & Development', 'Marketing', 'Sales Coordinators'];

// Priority label helper: D&D uses 1-5 scale (1=High, 2-3=Medium, 4-5=Low)
// Other depts keep 1-10 scale (1-3 High, 4-6 Medium, 7-10 Low)
const priorityLabelFor = (dept, p) => {
  const isDD = dept === DD_DEPT;
  if (isDD) {
    if (p === 1) return 'High';
    if (p <= 3) return 'Medium';
    return 'Low';
  }
  if (p <= 3) return 'High';
  if (p <= 6) return 'Medium';
  return 'Low';
};

export const TaskFormPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user, isSuperAdmin } = useAuth();
  const myDepartments = (user?.departments && user.departments.length) ? user.departments : (user?.department ? [user.department] : []);
  const isMultiDeptAdmin = user?.role === 'Admin' && myDepartments.length > 1;
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const initialProjectId = searchParams.get('project') || '';
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [teamMembers, setTeamMembers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]); // files staged during create
  const [uploadingFile, setUploadingFile] = useState(false);
  const [suggestingPriority, setSuggestingPriority] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [eligibleApprovers, setEligibleApprovers] = useState([]);
  const [showBrand, setShowBrand] = useState(false);
  const [isCreator, setIsCreator] = useState(true);
  const [deptConfig, setDeptConfig] = useState({ statuses: ['To Do', 'In Progress', 'Completed', 'Cancelled'], has_approval: false });
  
  const isMember = user?.role === 'Member';
  
  const [formData, setFormData] = useState({
    task_name: '', due_date: '', category: '', sub_category: '', brand: '', priority: 5,
    description: '', assigned_to: isMember ? (user?.id || '') : '', department: user?.department || '',
    status: 'To Do', work_type: 'Individual', assigned_to_list: [],
    approvers: [],
    is_recurring: false, repeat_type: '', project_id: initialProjectId,
  });
  const [completionErrors, setCompletionErrors] = useState({});

  const dept = formData.department || user?.department || '';
  const isDDDept = dept === DD_DEPT;
  const isApprovalDept = APPROVAL_DEPARTMENTS.includes(dept);
  const isProjectDept = PROJECT_DEPARTMENTS.includes(dept);
  const isHRDept = dept === 'Human Resources Management';
  const isAssignee = isEdit && (formData.assigned_to === user?.id || (formData.assigned_to_list || []).includes(user?.id));
  const isHigherRole = user?.role === 'Super Admin' || user?.role === 'Admin' || user?.role === 'Coordinator';
  // D&D special permissions: if task assigned by Admin/SA, Coordinator/Member can only edit description/attachments/status
  const isDDRestricted = isEdit && isDDDept && !isCreator && (
    (user?.role === 'Member') ||
    (user?.role === 'Coordinator' && formData._creatorRole && ['Admin', 'Super Admin'].includes(formData._creatorRole))
  );
  
  const canEditFull = isDDRestricted ? false : (!isEdit || isCreator || isAssignee || isHigherRole);
  const canEdit = canEditFull || isAssignee || isDDRestricted;
  const canEditDueDate = isDDRestricted ? false : (!isEdit || isCreator || isHigherRole);
  
  const isTeam = formData.work_type === 'Team';

  // Project scoping: legacy projects with collaborators restrict assignees; department projects don't
  const selectedProject = projects.find(p => p.id === formData.project_id) || null;
  const hasCollabs = selectedProject && (selectedProject.collaborator_details || []).length > 0;
  const projectMemberOptions = hasCollabs
    ? [
        ...(selectedProject.collaborator_details || []),
        ...((selectedProject.created_by && !(selectedProject.collaborator_details || []).some(c => c.id === selectedProject.created_by))
          ? [{ id: selectedProject.created_by, full_name: (selectedProject.created_by_name || 'Creator') + ' (Creator)', role: '', department: '' }]
          : []),
      ]
    : null;
  const assigneeBase = projectMemberOptions || teamMembers;
  const availableStatuses = isDDDept
    ? ['To Do', 'In Progress', 'Completed', 'Cancelled']
    : isHRDept
      ? ['To Do', 'In Progress', 'On Hold', 'Completed', 'Cancelled']
      : ['To Do', 'In Progress', 'Completed', 'Cancelled'];
  // Note: D&D members see "Completed" but backend auto-converts to "In Review"

  const isMarketing = dept === 'Marketing';
  const isSalesCoord = dept === 'Sales Coordinators';
  // Approvals available for all approval departments (D&D + Marketing + Sales Coordinators), regardless of project
  const approvalsAvailable = isApprovalDept;

  const noEntityDept = isMarketing || isDDDept || isSalesCoord;

  const validateCompletion = (status, cat, ent) => {
    if (status !== 'Completed') { setCompletionErrors({}); return true; }
    const errs = {};
    if (!cat) errs.category = true;
    if (isMarketing) {
      if (!formData.brand) errs.brand = true;
    } else if (isHRDept) {
      if (!formData.sub_category) errs.sub_category = true;
    } else if (!isDDDept && !isSalesCoord) {
      if (!ent) errs.entity = true;
    }
    setCompletionErrors(errs);
    if (Object.keys(errs).length > 0) {
      const missing = isMarketing ? 'Category and Brand' : isHRDept ? 'Category and Sub Category' : (isDDDept || isSalesCoord) ? 'Category' : 'Category and Entity';
      toast.error(`Please fill ${missing} before marking task as Completed`);
      return false;
    }
    return true;
  };

  const handleStatusChange = (v) => {
    if (v === 'Completed') {
      const cat = formData.category || '';
      if (isMarketing) {
        const brand = formData.brand || '';
        if (!cat || !brand) {
          setCompletionErrors({ category: !cat, brand: !brand });
          toast.error('Please fill Category and Brand before marking task as Completed');
          return;
        }
      } else if (isHRDept) {
        const sub = formData.sub_category || '';
        if (!cat || !sub) {
          setCompletionErrors({ category: !cat, sub_category: !sub });
          toast.error('Please fill Category and Sub Category before marking task as Completed');
          return;
        }
      } else if (!isDDDept && !isSalesCoord) {
        const ent = formData.entity || '';
        if (!cat || !ent) {
          setCompletionErrors({ category: !cat, entity: !ent });
          toast.error('Please fill Category and Entity before marking task as Completed');
          return;
        }
      } else {
        if (!cat) {
          setCompletionErrors({ category: true });
          toast.error('Please fill Category before marking task as Completed');
          return;
        }
      }
    }
    if (v === 'Cancelled') {
      if (!formData.description || !formData.description.trim()) {
        setCompletionErrors({ description: true });
        toast.error('Please add a description before cancelling the task');
        return;
      }
    }
    setCompletionErrors({});
    setFormData({ ...formData, status: v });
  };

  useEffect(() => {
    fetchTeamMembers();
    fetchCategories();
    fetchProjects();
    if (isEdit) fetchTask();
  }, [id]);

  // Load eligible approvers (approval departments) whenever the department changes
  useEffect(() => {
    if (!APPROVAL_DEPARTMENTS.includes(formData.department)) { setEligibleApprovers([]); return; }
    getEligibleApprovers(formData.department).then(r => setEligibleApprovers(r.data || [])).catch(() => setEligibleApprovers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.department]);

  useEffect(() => {
    if (formData.department) {
      if (isSuperAdmin || isMultiDeptAdmin) fetchTeamMembers();
      fetchCategories();
    }
  }, [formData.department]);

  // Fetch sub-categories when department is HR or D&D + category changes
  useEffect(() => {
    const loadSubs = async () => {
      if ((isHRDept || isDDDept) && formData.category) {
        try {
          const res = await getSubcategories({ department: dept, category: formData.category });
          setSubCategories(res.data || []);
        } catch { setSubCategories([]); }
      } else {
        setSubCategories([]);
      }
    };
    loadSubs();
  }, [formData.category, isHRDept, isDDDept, dept]);

  const fetchProjects = async () => {
    try {
      const res = await getProjects();
      setProjects(res.data || []);
    } catch { setProjects([]); }
  };

  const fetchCategories = async () => {    const d = formData.department || user?.department;
    if (!d) return;
    try {
      const res = await getDepartmentCategories(d);
      setCategories(res.data.categories || []);
      setShowBrand(res.data.show_brand || false);
    } catch { setCategories([]); setShowBrand(false); }
  };

  const fetchTeamMembers = async () => {
    try {
      const params = { status: 'Active' };
      if (isSuperAdmin || isMultiDeptAdmin) {
        if (formData.department) params.department = formData.department;
      } else {
        params.department = user?.department;
      }
      const response = await getUsers(params);
      setTeamMembers(response.data);
    } catch (error) { console.error('Failed to fetch team members'); }
  };

  const fetchTask = async () => {
    try {
      const response = await getTask(id);
      const task = response.data;
      setFormData({
        task_name: task.task_name, due_date: task.due_date, category: task.category,
        sub_category: task.sub_category || '',
        brand: task.brand || '', priority: task.priority, description: task.description,
        assigned_to: task.assigned_to, department: task.department, status: task.status,
        entity: task.entity || '', work_type: task.work_type || 'Individual',
        assigned_to_list: task.assigned_to_list || [],
        approvers: task.approvers || [],
        is_recurring: !!task.is_recurring,
        repeat_type: task.repeat_type || '',
        project_id: task.project_id || '',
        _creatorRole: task._creatorRole || '',
      });
      setAttachments(task.attachments || []);
      setIsCreator(task.created_by === user?.id);
      // Fetch creator role for D&D permission check
      if (APPROVAL_DEPARTMENTS.includes(task.department) && task.created_by !== user?.id) {
        try {
          const ures = await getUsers({ status: 'Active' });
          const creator = ures.data.find(u => u.id === task.created_by);
          if (creator) setFormData(prev => ({ ...prev, _creatorRole: creator.role }));
        } catch {}
      }
    } catch (error) {
      toast.error('Failed to fetch task');
      navigate('/tasks');
    } finally { setFetchLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const needsAssignee = isTeam ? (formData.assigned_to_list || []).length === 0 : !formData.assigned_to;
    if (!formData.task_name || !formData.due_date || needsAssignee) {
      toast.error('Please fill Task Name, Due Date, and Assign To');
      return;
    }
    if (formData.status === 'Completed' && !validateCompletion(formData.status, formData.category, formData.entity)) {
      return;
    }
    if (formData.status === 'Cancelled' && (!formData.description || !formData.description.trim())) {
      setCompletionErrors({ description: true });
      toast.error('Please add a description before cancelling the task');
      return;
    }
    if (isHRDept && formData.is_recurring && !formData.repeat_type) {
      toast.error('Please select a Repeat Type for recurring task');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData };
      delete payload._creatorRole;
      if (!isMarketing) payload.brand = '';
      if (isTeam) {
        payload.assigned_to = (formData.assigned_to_list || [])[0] || '';
        payload.assigned_to_list = formData.assigned_to_list || [];
      } else {
        payload.assigned_to_list = [];
      }
      
      if (isEdit && !canEdit) {
        toast.error('You do not have permission to update this task');
        return;
      } else if (isEdit) {
        await updateTask(id, payload);
      } else {
        const createdRes = await createTask(payload);
        // Register any staged S3 attachments after create
        const newId = createdRes?.data?.id;
        if (newId && pendingFiles.length > 0) {
          for (const meta of pendingFiles) {
            try { await registerS3Attachment(newId, meta); }
            catch { toast.error(`Failed to attach ${meta.filename}`); }
          }
        }
      }
      toast.success(isEdit ? 'Task updated' : 'Task created');
      navigate('/tasks');
    } catch (error) {
      toast.error(
        typeof error.response?.data?.detail === 'string' 
          ? error.response.data.detail 
          : Array.isArray(error.response?.data?.detail)
            ? error.response.data.detail.map(e => e.msg).join(', ')
            : `Failed to ${isEdit ? 'update' : 'create'} task`
      );
    } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate sizes
    for (const f of files) {
      if (f.size > 500 * 1024 * 1024) {
        toast.error(`"${f.name}" is too large. Max 500MB`);
        return;
      }
    }

    setUploadingFile(true);
    try {
      for (const file of files) {
        const ct = file.type || 'application/octet-stream';
        // 1) Ask backend for a presigned S3 PUT URL
        const pres = await presignAttachment({ filename: file.name, content_type: ct, size: file.size });
        // 2) Upload the file directly to S3
        await uploadToS3(pres.data.upload_url, file, ct);
        const meta = { key: pres.data.key, filename: file.name, content_type: ct, size: file.size };
        if (isEdit) {
          // 3) Register the attachment on the task
          const reg = await registerS3Attachment(id, meta);
          setAttachments(prev => [...prev, reg.data]);
        } else {
          // Stage metadata; registered after the task is created
          setPendingFiles(prev => [...prev, meta]);
        }
      }
      toast.success(`${files.length} file(s) uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally { setUploadingFile(false); e.target.value = ''; }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await deleteAttachment(id, attachmentId);
      setAttachments(attachments.filter(a => a.id !== attachmentId));
      toast.success('Attachment deleted');
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleSuggestPriority = async () => {
    if (!formData.task_name || !formData.category) { toast.error('Enter task name and category first'); return; }
    setSuggestingPriority(true);
    try {
      const response = await aiSuggestPriority({
        task_name: formData.task_name, description: formData.description,
        category: formData.category, department: dept,
      });
      const suggested = response.data.priority;
      if (suggested >= 1 && suggested <= 10) {
        setFormData(prev => ({ ...prev, priority: suggested }));
        toast.success(`AI suggests priority ${suggested}: ${response.data.reason || ''}`);
      }
    } catch (error) { toast.error('Failed to get suggestion'); }
    finally { setSuggestingPriority(false); }
  };

  if (fetchLoading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div></div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="task-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild><Link to="/tasks"><ArrowLeft className="h-5 w-5" /></Link></Button>
        <div>
          <h1 className="text-2xl font-heading font-bold">{isEdit ? 'Edit Task' : 'Create New Task'}</h1>
          <p className="text-muted-foreground">{isEdit ? (canEditFull ? 'Update task details' : 'Update status, category, brand, description and more') : 'Fill in the details to create a new task'}</p>
        </div>
      </div>


      <form onSubmit={handleSubmit}>
        <Card className="border-border/50">
          <CardContent className={`p-6 ${isDDDept ? 'flex flex-col gap-6' : 'space-y-6'}`}>
            {/* Task Name */}
            <div className={`space-y-2 ${isDDDept ? 'order-3' : ''}`}>
              <Label>Task Name *</Label>
              <Input value={formData.task_name} onChange={(e) => setFormData({ ...formData, task_name: e.target.value })} placeholder="Enter task name" required disabled={!canEditFull} data-testid="task-name-input" />
            </div>

            {/* Project (optional) — departments with Projects enabled */}
            {!isEdit && isProjectDept && projects.length > 0 && (
              <div className="space-y-2">
                <Label>Project</Label>
                <Select
                  value={formData.project_id || 'none'}
                  disabled={!!initialProjectId}
                  onValueChange={(v) => setFormData({ ...formData, project_id: v === 'none' ? '' : v, assigned_to: isMember ? (user?.id || '') : '', assigned_to_list: [] })}
                >
                  <SelectTrigger data-testid="project-select"><SelectValue placeholder="Open (no project)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Open (no project)</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {initialProjectId
                  ? <p className="text-xs text-muted-foreground">This task is locked to the selected project.</p>
                  : <p className="text-xs text-muted-foreground">Choose a project, or leave it "Open" for a standalone task.</p>}
              </div>
            )}
            {isEdit && formData.project_id && selectedProject && (
              <div className="flex items-center gap-2 p-2 rounded-md bg-[#F5A623]/10 border border-[#F5A623]/20 text-sm" data-testid="task-project-badge">
                <FolderKanban className="h-4 w-4 text-[#F5A623]" /> Project: {selectedProject.name}
              </div>
            )}

            {/* Department picker (Super Admin = all depts; multi-dept Admin = their depts) */}
            {(isSuperAdmin || isMultiDeptAdmin) && !isEdit && (
              <div className={`space-y-2 ${isDDDept ? 'order-0' : ''}`}>
                <Label>Department *</Label>
                <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v, assigned_to: '', category: '', brand: '' })}>
                  <SelectTrigger data-testid="department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>{(isSuperAdmin ? DEPARTMENTS : myDepartments).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            {/* Due Date */}
            <div className={`space-y-2 ${isDDDept ? 'order-4' : ''}`}>
              <Label>Due Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !formData.due_date && "text-muted-foreground")} disabled={!canEditDueDate} data-testid="due-date-picker">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.due_date ? format(new Date(formData.due_date), "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={formData.due_date ? new Date(formData.due_date) : undefined} onSelect={(date) => setFormData({ ...formData, due_date: date?.toISOString() || '' })} disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            {/* Category & Brand (department-aware) - Optional */}
            <div className={`grid grid-cols-1 ${(showBrand || isMarketing) ? 'md:grid-cols-2' : ''} gap-4 ${isDDDept ? 'order-1' : ''}`}>
              <div className="space-y-2">
                <Label className={completionErrors.category ? 'text-destructive' : ''}>
                  Category {completionErrors.category && <span className="text-xs ml-1">(Required for Completed)</span>}
                </Label>
                <Select value={formData.category || "none"} onValueChange={(v) => { setFormData({ ...formData, category: v === "none" ? "" : v, sub_category: '' }); if (completionErrors.category) setCompletionErrors(prev => ({...prev, category: false})); }}>
                  <SelectTrigger className={completionErrors.category ? 'border-destructive ring-1 ring-destructive' : ''} data-testid="category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                {completionErrors.category && (
                  <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Required to mark as Completed</p>
                )}
              </div>
              {isMarketing && (
                <div className="space-y-2">
                  <Label className={completionErrors.brand ? 'text-destructive' : ''}>
                    Brand {completionErrors.brand && <span className="text-xs ml-1">(Required for Completed)</span>}
                  </Label>
                  <Select value={formData.brand || "none"} onValueChange={(v) => { setFormData({ ...formData, brand: v === "none" ? "" : v }); if (completionErrors.brand) setCompletionErrors(prev => ({...prev, brand: false})); }}>
                    <SelectTrigger className={completionErrors.brand ? 'border-destructive ring-1 ring-destructive' : ''} data-testid="brand-select"><SelectValue placeholder="Select brand" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {completionErrors.brand && (
                    <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Required to mark as Completed</p>
                  )}
                </div>
              )}
            </div>

            {/* Sub Category - HR (mandatory on Complete) + D&D (optional) */}
            {(isHRDept || isDDDept) && (
              <div className={`space-y-2 ${isDDDept ? 'order-2' : ''}`}>
                <Label className={completionErrors.sub_category ? 'text-destructive' : ''}>
                  Sub Category {isHRDept && completionErrors.sub_category && <span className="text-xs ml-1">(Required for Completed)</span>}
                  {isDDDept && <span className="text-xs ml-1 text-muted-foreground">(Optional)</span>}
                </Label>
                <Select
                  value={formData.sub_category || "none"}
                  onValueChange={(v) => {
                    setFormData({ ...formData, sub_category: v === "none" ? "" : v });
                    if (completionErrors.sub_category) setCompletionErrors(prev => ({ ...prev, sub_category: false }));
                  }}
                  disabled={!formData.category}
                >
                  <SelectTrigger className={completionErrors.sub_category ? 'border-destructive ring-1 ring-destructive' : ''} data-testid="sub-category-select">
                    <SelectValue placeholder={formData.category ? 'Select sub-category' : 'Select a category first'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {subCategories.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {formData.category && subCategories.length === 0 && (
                  <p className="text-xs text-muted-foreground">No sub-categories defined for "{formData.category}". Ask Super Admin to add them in Sub-Categories page.</p>
                )}
                {completionErrors.sub_category && isHRDept && (
                  <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Required to mark as Completed</p>
                )}
              </div>
            )}

            {/* Entity - Optional (Non-Marketing, Non-D&D only) */}
            {!isMarketing && dept !== 'Design & Development' && !isSalesCoord && (
            <div className="space-y-2">
              <Label className={completionErrors.entity ? 'text-destructive' : ''}>
                Entity {completionErrors.entity && <span className="text-xs ml-1">(Required for Completed)</span>}
              </Label>
              <Select value={formData.entity || "none"} onValueChange={(v) => { setFormData({ ...formData, entity: v === "none" ? "" : v }); if (completionErrors.entity) setCompletionErrors(prev => ({...prev, entity: false})); }}>
                <SelectTrigger className={completionErrors.entity ? 'border-destructive ring-1 ring-destructive' : ''} data-testid="entity-select"><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  <SelectItem value="UOSPL">UOSPL</SelectItem>
                  <SelectItem value="UAPL">UAPL</SelectItem>
                  <SelectItem value="UAFPL">UAFPL</SelectItem>
                  <SelectItem value="United Group">United Group</SelectItem>
                </SelectContent>
              </Select>
              {completionErrors.entity && (
                <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Required to mark as Completed</p>
              )}
            </div>
            )}

            {/* Priority */}
            <div className={`space-y-2 ${isDDDept ? 'order-5' : ''}`}>
              <div className="flex items-center justify-between">
                <Label>Priority * {isDDDept ? '(1 = High, 2-3 = Medium, 4-5 = Low)' : '(1 = Highest, 10 = Lowest)'}</Label>
                {canEditFull && (
                  <Button type="button" variant="ghost" size="sm" onClick={handleSuggestPriority} disabled={suggestingPriority} className="text-xs h-7 text-[#F5A623] hover:text-[#D48C1B]" data-testid="ai-suggest-priority-btn">
                    <Sparkles className="h-3 w-3 mr-1" />{suggestingPriority ? 'Analyzing...' : 'AI Suggest'}
                  </Button>
                )}
              </div>
              <Select value={formData.priority.toString()} onValueChange={(v) => setFormData({ ...formData, priority: parseInt(v) })} disabled={!canEditFull}>
                <SelectTrigger data-testid="priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(isDDDept ? [1,2,3,4,5] : [1,2,3,4,5,6,7,8,9,10]).map(p =>
                    <SelectItem key={p} value={p.toString()}>{p} ({priorityLabelFor(dept, p)})</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Work Type — D&D only */}
            {!isEdit && isDDDept && (
              <div className="space-y-2 order-6">
                <Label>Work Type</Label>
                <Select value={formData.work_type} onValueChange={(v) => setFormData({
                  ...formData,
                  work_type: v,
                  assigned_to: v === 'Team' ? (isMember ? user?.id : '') : (isMember ? user?.id : ''),
                  assigned_to_list: v === 'Team' && isMember ? [user?.id] : [],
                })}>
                  <SelectTrigger data-testid="work-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Individual">Individual</SelectItem>
                    <SelectItem value="Team">Team</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {isEdit && isDDDept && formData.work_type === 'Team' && (
              <div className="flex items-center gap-2 p-2 rounded-md bg-blue-500/10 border border-blue-500/20 text-sm text-blue-700 dark:text-blue-400 order-6">
                <Users className="h-4 w-4" /> Team Task
              </div>
            )}

            {/* Assign To */}
            <div className={`space-y-2 ${isDDDept ? 'order-7' : ''}`}>
              <Label>Assign To *</Label>
              {isMember && !isEdit && !(isDDDept && isTeam) ? (
                <div className="flex items-center h-10 px-3 rounded-md border border-input bg-muted/50 text-sm" data-testid="assignee-self-locked">
                  {user?.full_name} (You)
                </div>
              ) : isTeam ? (
                <div className="space-y-2" data-testid="team-assign-list">
                  <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-1">
                    {assigneeBase.filter(m => {
                      // Exclude Super Admins always
                      if (m.role === 'Super Admin') return false;
                      // Members (D&D Team) can only pick same-dept Coordinators/Members
                      if (isMember) {
                        if (m.department !== user?.department) return false;
                        return m.role !== 'Admin';
                      }
                      // Higher roles
                      if (user?.role === 'Super Admin' || user?.role === 'Admin') return true;
                      return m.role !== 'Admin';
                    }).map(m => {
                      const isSelf = m.id === user?.id;
                      return (
                        <label key={m.id} className={`flex items-center gap-2 p-1.5 rounded hover:bg-accent cursor-pointer text-sm ${isSelf ? 'bg-[#F5A623]/10' : ''}`}>
                          <Checkbox
                            checked={(formData.assigned_to_list || []).includes(m.id)}
                            onCheckedChange={(checked) => {
                              const list = formData.assigned_to_list || [];
                              const newList = checked ? [...list, m.id] : list.filter(id => id !== m.id);
                              setFormData({ ...formData, assigned_to_list: newList, assigned_to: newList[0] || '' });
                            }}
                            disabled={!canEditFull && !(!isEdit)}
                            data-testid={`team-assign-${m.id}`}
                          />
                          <span>{m.full_name} ({m.role}){isSelf && <span className="ml-1 text-[10px] text-[#F5A623] font-semibold">(You)</span>}</span>
                        </label>
                      );
                    })}
                  </div>
                  {(formData.assigned_to_list || []).length > 0 && (
                    <p className="text-xs text-muted-foreground">{(formData.assigned_to_list || []).length} member(s) selected</p>
                  )}
                </div>
              ) : (
                <Select value={formData.assigned_to} onValueChange={(v) => setFormData({ ...formData, assigned_to: v })} disabled={!canEditFull && !(!isEdit)}>
                  <SelectTrigger data-testid="assigned-to-select"><SelectValue placeholder="Select team member" /></SelectTrigger>
                  <SelectContent>{assigneeBase.filter(m => {
                    if (m.role === 'Super Admin') return false;
                    if (user?.role === 'Super Admin' || user?.role === 'Admin') return true;
                    return m.role !== 'Admin';
                  }).map(m => <SelectItem key={m.id} value={m.id}>{m.full_name} ({m.role})</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>

            {/* Approvals — shown when the task uses the approval workflow */}
            {approvalsAvailable && (
              <div className={`space-y-2 ${isDDDept ? 'order-9' : ''}`} data-testid="approvers-section">
                <Label>Approvals (optional)</Label>
                <div className="border rounded-md p-3 max-h-44 overflow-y-auto space-y-1">
                  {eligibleApprovers.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No Coordinators or Admins available in this department.</p>
                  ) : eligibleApprovers.map(a => (
                    <label key={a.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-accent cursor-pointer text-sm">
                      <Checkbox
                        checked={(formData.approvers || []).includes(a.id)}
                        onCheckedChange={() => {
                          const cur = formData.approvers || [];
                          setFormData({ ...formData, approvers: cur.includes(a.id) ? cur.filter(x => x !== a.id) : [...cur, a.id] });
                        }}
                        data-testid={`approver-option-${a.id}`}
                      />
                      <span className="flex-1">{a.full_name}</span>
                      <span className="text-[10px] text-muted-foreground">{a.role}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Only Coordinators and Admins of this department are listed. If selected, approval can come from any one of them. If none is selected, any department Admin can approve.
                </p>
              </div>
            )}

            {/* Status (Edit only) — always editable */}
            {isEdit && (
              <div className={`space-y-2 ${isDDDept ? 'order-9' : ''}`}>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={handleStatusChange}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{availableStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                {approvalsAvailable && formData.status !== 'In Review' && user?.role !== 'Admin' && user?.role !== 'Super Admin' && (
                  <p className="text-xs text-muted-foreground">Selecting "Completed" will send the task for approval</p>
                )}
              </div>
            )}

            {/* HR Recurring Task — HR only */}
            {isHRDept && (
              <div className="space-y-3 p-4 rounded-lg border border-border bg-accent/30" data-testid="recurring-section">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="recurring-toggle"
                    checked={!!formData.is_recurring}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      is_recurring: !!checked,
                      repeat_type: checked ? (formData.repeat_type || 'Monthly') : '',
                    })}
                    disabled={!canEditFull}
                    data-testid="is-recurring-checkbox"
                  />
                  <Label htmlFor="recurring-toggle" className="cursor-pointer">
                    Recurring Task
                    <span className="ml-2 text-xs text-muted-foreground">Auto-generate the next task on completion</span>
                  </Label>
                </div>
                {formData.is_recurring && (
                  <div className="space-y-2 pl-7">
                    <Label>Repeat Type *</Label>
                    <Select
                      value={formData.repeat_type || ''}
                      onValueChange={(v) => setFormData({ ...formData, repeat_type: v })}
                      disabled={!canEditFull}
                    >
                      <SelectTrigger className="w-full md:w-60" data-testid="repeat-type-select">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Weekly">Weekly</SelectItem>
                        <SelectItem value="Monthly">Monthly</SelectItem>
                        <SelectItem value="Quarterly">Quarterly</SelectItem>
                        <SelectItem value="Yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div className={`space-y-2 ${isDDDept ? 'order-10' : ''}`}>
              <Label className={completionErrors.description ? 'text-destructive' : ''}>
                Description {completionErrors.description && <span className="text-xs ml-1">(Required for Cancelled)</span>}
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => { setFormData({ ...formData, description: e.target.value }); if (completionErrors.description) setCompletionErrors(prev => ({...prev, description: false})); }}
                placeholder="Enter description..."
                rows={4}
                className={completionErrors.description ? 'border-destructive ring-1 ring-destructive' : ''}
                data-testid="description-input"
              />
              {completionErrors.description && (
                <p className="text-xs text-destructive flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Required to cancel task</p>
              )}
            </div>

            {/* Attachments — available in create & edit for all departments (S3-backed) */}
            {true && (
              <div className={`space-y-4 ${isDDDept ? 'order-8' : ''}`}>
                <Label className="flex items-center gap-2"><Paperclip className="h-4 w-4" /> Attachments</Label>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileUpload}
                      accept=".dwg,.dxf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.tif,.tiff,.jpg,.jpeg,.png,.gif,.webp,.csv,.sldprt,.sldasm,.slddrw,.max,.skp,.rvt,.rfa,.ifc,.f3d,.f3z,.3dm"
                      disabled={uploadingFile}
                      data-testid="file-upload-input"
                    />
                    <Button type="button" variant="outline" disabled={uploadingFile} asChild>
                      <span><Upload className="h-4 w-4 mr-2" />{uploadingFile ? 'Uploading...' : 'Upload Files'}</span>
                    </Button>
                  </label>
                  <span className="text-xs text-muted-foreground">AutoCAD, MS Office, PDF, TIF, JPEG, SolidWorks, 3ds Max, SketchUp, Revit, BIM, Fusion 360, Rhino · up to 500MB</span>
                </div>
                {/* Staged files (create mode) */}
                {!isEdit && pendingFiles.length > 0 && (
                  <div className="space-y-2" data-testid="staged-files">
                    {pendingFiles.map((f, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-accent/50">
                        <div className="flex items-center gap-3">
                          <File className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{f.filename}</p>
                            <p className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(1)} KB · uploaded</p>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}>
                          <X className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Uploaded attachments (edit mode) */}
                {isEdit && attachments.length > 0 && (
                  <div className="space-y-2">
                    {attachments.map((att) => (
                      <div key={att.id} className="flex items-center justify-between p-3 rounded-lg bg-accent/50">
                        <div className="flex items-center gap-3">
                          <File className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{att.filename}</p>
                            <p className="text-xs text-muted-foreground">{(att.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => handleDeleteAttachment(att.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className={`flex justify-end gap-4 pt-4 border-t border-border ${isDDDept ? 'order-11' : ''}`}>
              <Button type="button" variant="outline" asChild><Link to="/tasks">Cancel</Link></Button>
              <Button type="submit" className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" disabled={loading} data-testid="submit-task-btn">
                {loading ? 'Saving...' : isEdit ? 'Update Task' : 'Create Task'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};
